/**
 * useMessagesBootstrap — shared Layout-level messaging + AI-conversation
 * data fetched in a single request via `/api/messages/bootstrap`.
 *
 * The Layout unread badge, the MessagesCenter header dropdown, and the
 * AI-assistant ConversationContext all read from this shared query so a
 * fresh authenticated page load consumes ONE rate-limit slot instead of
 * three independent fetches.
 *
 * Each field is role-gated on the backend and will be `null` for users
 * who should not see it — callers treat `null` as "don't render the
 * corresponding panel".
 */
import { useApiGet } from './useApiQuery';
import { useAuthReady } from './useAuthReady';
import { isClientRole } from '../utils/roleConstants';

export interface MessagesBootstrapInboxMessage {
  id: string;
  patient_id?: string;
  patient_name?: string;
  subject?: string;
  preview?: string;
  sender_name?: string;
  sender_role?: string;
  timestamp?: string;
  is_unread?: boolean;
  unread_count?: number;
  is_starred?: boolean;
  is_archived?: boolean;
}

export interface MessagesBootstrapInboxPreview {
  messages: MessagesBootstrapInboxMessage[];
}

export interface MessagesBootstrapUnreadCount {
  care_unread: number;
  team_unread: number;
  notifications_unread: number;
  unread_count: number;
}

export interface MessagesBootstrapRecentConversation {
  id: string;
  title: string;
  created_at: string | null;
  last_updated: string | null;
}

export interface MessagesBootstrap {
  unread_count: MessagesBootstrapUnreadCount | null;
  inbox_preview: MessagesBootstrapInboxPreview | null;
  recent_conversations: MessagesBootstrapRecentConversation[] | null;
}

export const MESSAGES_BOOTSTRAP_QUERY_KEY = ['messages', 'bootstrap'] as const;

/**
 * Returns the cached messages bootstrap payload.  Multiple callers
 * share a single network request via React Query's single-flight
 * deduplication.  Safe to call from any Layout-level component or
 * context provider.
 *
 * Poll interval mirrors the legacy independent `/api/messages/unread-count`
 * fetch so unread badges stay roughly fresh without any per-component
 * timers.  Paused when the tab is hidden to avoid background churn.
 */
export function useMessagesBootstrap() {
  const { ready, user } = useAuthReady();
  const userRoles = user ? (user.roles || (user.role ? [user.role] : [])) : [];
  const enabled = Boolean(ready && user && !isClientRole(userRoles));
  return useApiGet<MessagesBootstrap>(
    enabled ? MESSAGES_BOOTSTRAP_QUERY_KEY : null,
    '/api/messages/bootstrap',
    {
      staleTime: 60 * 1000,
      refetchInterval: 60 * 1000,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      retry: false,
    },
  );
}
